/**
 * Roster role classification — the browser-side mirror of
 * `backend/config/rosterRoles.ts`.
 *
 * Roster access is expressed entirely through role names, in six shapes:
 *
 *   rosters:admin              capability — claim, publish, configure
 *   rosters:viewer             capability — read-only across every site
 *   rosters:default            capability — worker self-view (My Shifts) only
 *   rosters:<siteCode>         scope      — read + write for one site
 *   rosters:view:<siteCode>    scope      — read-only for one site
 *   rosters:region:<region>    scope      — read + write for every site in a region
 *
 * Scope roles STACK: a supervisor holding `rosters:32-sb` and `rosters:32-cb`
 * may edit both basins. The effective permission set is the union.
 *
 * A browser bundle cannot import backend config, so this is a deliberate copy.
 * Both sides are covered by unit tests asserting the same table of cases — if you
 * change one, change the other or CI fails.
 *
 * Every gate in the UI is a convenience, never a control: the backend's
 * requireRosterRole middleware is what actually enforces this. The point of
 * getting the UI right is that a granted role should lead somewhere, and a role
 * that is not granted should not advertise a screen that will 403.
 */

export const ROSTER_ROLE_PREFIX = 'rosters:';

/** Second segments that are NEVER a site code. See the backend module for why. */
export const ROSTER_RESERVED_SEGMENTS: readonly string[] = [
  'admin',
  'default',
  'viewer',
  'view',
  'region',
];

export type RosterRoleKind =
  | 'admin'
  | 'default'
  | 'global-viewer'
  | 'site-edit'
  | 'site-view'
  | 'region';

export interface ParsedRosterRole {
  kind: RosterRoleKind;
  /** Present for 'site-edit' and 'site-view'. Lower-cased. */
  siteCode?: string;
  /** Present for 'region'. Lower-cased. */
  region?: string;
}

/**
 * Classify a role name. Returns null for anything that is not a roster role, and
 * for malformed roster names — an unparseable name grants nothing.
 */
export function parseRosterRole(roleName: unknown): ParsedRosterRole | null {
  const name = String(roleName ?? '')
    .trim()
    .toLowerCase();
  if (!name.startsWith(ROSTER_ROLE_PREFIX)) return null;

  const parts = name.split(':');

  if (parts.length === 2) {
    const segment = parts[1];
    if (!segment) return null;
    if (segment === 'admin') return { kind: 'admin' };
    if (segment === 'default') return { kind: 'default' };
    if (segment === 'viewer') return { kind: 'global-viewer' };
    if (ROSTER_RESERVED_SEGMENTS.includes(segment)) return null;
    return { kind: 'site-edit', siteCode: segment };
  }

  if (parts.length === 3 && parts[2]) {
    if (parts[1] === 'view') return { kind: 'site-view', siteCode: parts[2] };
    if (parts[1] === 'region') return { kind: 'region', region: parts[2] };
  }

  return null;
}

/** What a user's roles let them do in the roster module. */
export interface RosterAccess {
  /** Platform admin, or `rosters:admin` — the full management surface. */
  isAdmin: boolean;
  /** May open a roster and change it (admin, any site-edit role, or a region role). */
  canEdit: boolean;
  /** May open a roster read-only, and holds nothing that lets them edit. */
  isViewerOnly: boolean;
  /** May reach the roster module at all — the module-level gate. */
  canAccess: boolean;
  /** True when the user's read access is not limited to particular sites. */
  hasAllSites: boolean;
  /** Sites this user may edit, lower-cased. Empty for admins (claim-scoped) and viewers. */
  editSiteCodes: string[];
  /** Sites this user may view read-only, lower-cased. */
  viewSiteCodes: string[];
  /** Regions granting edit access, lower-cased. Expanded to sites by the backend. */
  regions: string[];
}

const PLATFORM_ADMIN_ROLES = ['admin', 'platform:admin', 'platform_admin'];

/**
 * Resolve a user's roster access from their role names.
 *
 * `canEdit` deliberately includes region roles even though this side cannot
 * expand a region to its sites — that expansion needs SiteMapping, which only the
 * backend has. The site directory's per-row `canAccess` flag is what scopes the
 * picker; this only decides whether the board opens in edit or read-only mode.
 */
export function resolveRosterAccess(roleNames: readonly unknown[] = []): RosterAccess {
  const names = roleNames.map(r =>
    String(r ?? '')
      .trim()
      .toLowerCase()
  );

  const isPlatformAdmin = names.some(n => PLATFORM_ADMIN_ROLES.includes(n));

  const editSites = new Set<string>();
  const viewSites = new Set<string>();
  const regions = new Set<string>();
  let isRosterAdmin = false;
  let isGlobalViewer = false;
  let hasDefault = false;

  for (const name of names) {
    const parsed = parseRosterRole(name);
    if (!parsed) continue;
    switch (parsed.kind) {
      case 'admin':
        isRosterAdmin = true;
        break;
      case 'global-viewer':
        isGlobalViewer = true;
        break;
      case 'default':
        hasDefault = true;
        break;
      case 'site-edit':
        editSites.add(parsed.siteCode as string);
        break;
      case 'site-view':
        viewSites.add(parsed.siteCode as string);
        break;
      case 'region':
        regions.add(parsed.region as string);
        break;
    }
  }

  const isAdmin = isPlatformAdmin || isRosterAdmin;
  const canEdit = isAdmin || editSites.size > 0 || regions.size > 0;
  const canView = isGlobalViewer || viewSites.size > 0;

  return {
    isAdmin,
    canEdit,
    isViewerOnly: !canEdit && canView,
    canAccess: canEdit || canView || hasDefault,
    hasAllSites: isPlatformAdmin || isGlobalViewer,
    editSiteCodes: [...editSites],
    viewSiteCodes: [...viewSites],
    regions: [...regions],
  };
}

/** True when the role name is a per-site roster role (editor or viewer). */
export function isRosterSiteRole(roleName: unknown): boolean {
  const kind = parseRosterRole(roleName)?.kind;
  return kind === 'site-edit' || kind === 'site-view';
}
